/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type AfterContentChecked, type OnInit, Component, ElementRef, EventEmitter, Output, ViewChild } from '@angular/core';
import { EventService, LazyloadService } from '@dm/core';
import { EventServiceKeyE } from 'app/common/constants/event-service-key';
import { AuthService } from 'app/common/services/auth.service';
import { SWITCH } from 'app/config/switch';

import { SmartAssistantService } from '../smart-assistant/smart-assistant.service';
import { SIMPLE_SWITCH_BG_URL, SimpleModeTipE, SimpleModeTipNumE, SMART_ASSISTANT_BG_URL } from './simple-mode-tip.const';
import { SimpleModeTipStrService } from './simple-mode-tip-str.service';

@Component({
    selector: 'app-simple-mode-tip',
    templateUrl: './simple-mode-tip.component.html',
    styleUrls: ['./simple-mode-tip.component.scss'],
})
export class SimpleModeTipComponent implements OnInit, AfterContentChecked {
    /**
     * 关闭提示框
     */
    @Output() onClose = new EventEmitter<void>();
    /**
     * 图标高亮圆圈
     */
    @ViewChild('circle') circle: ElementRef;
    /**
     * CSS三角形
     */
    @ViewChild('triangle') triangle: ElementRef;
    /**
     * 资料
     */
    i18n = this.str.i18n;
    /**
     * 提示框背景图路径
     */
    backgroundImgUrl = SIMPLE_SWITCH_BG_URL;
    /**
     * 提示标题
     */
    tipTitle = this.i18n.switchTitle;
    /**
     * 提示内容
     */
    tipContent = this.i18n.switchContent;
    /**
     * 下一步按钮文字
     */
    btnLabel = SWITCH.IS_SIMPLE_MODE ? this.i18n.nextBtn : this.i18n.finishBtn;
    /**
     * 提示数量（极简模式2条，标准模式1条）
     */
    tipsNum = SWITCH.IS_SIMPLE_MODE ? SimpleModeTipNumE.Simple : SimpleModeTipNumE.Standard;
    /**
     * 当前显示的tip
     */
    currentTip = SimpleModeTipE.SimpleModeSwitch;
    /**
     * 是否可见
     */
    visible = true;
    /**
     * 圆圈的背景颜色和图标颜色
     */
    circleColor = {
        backGround: SWITCH.IS_SIMPLE_MODE ? '#F0F3FA' : '#282B33',
        icon: SWITCH.IS_SIMPLE_MODE ? '#282B33' : '#A7ABBE',
    };
    /**
     * “不再显示”按钮被点击后隐藏
     */
    notShowBtnVisible = true;
    /**
     * 常量
     */
    SWITCH = SWITCH;
    SimpleModeTipE = SimpleModeTipE;

    constructor(
        private str: SimpleModeTipStrService,
        private lazyloadService: LazyloadService,
        private authService: AuthService,
        private eventService: EventService,
        private smartAssistantService: SmartAssistantService,
    ) { }

    ngOnInit(): void {
        this.updatePosition();
    }

    ngAfterContentChecked(): void {
        this.updatePosition();
    }

    /**
     * 根据当前Tip的条数和顶部图标位置，调整Tip的定位
     */
    updatePosition(): void {
        // 提示框元素
        const tipEl: HTMLElement = document.querySelector('#simple-mode-tip .n-dialog-container');

        // 第一条提示：极简模式切换按钮
        if (this.currentTip === SimpleModeTipE.SimpleModeSwitch) {
            const simpleModeSwitchEl = document.querySelector('#simple-mode-switch');
            if (simpleModeSwitchEl) {
                tipEl.style.top = `${simpleModeSwitchEl.getBoundingClientRect().top + 40}px`;
                tipEl.style.left = `${simpleModeSwitchEl.getBoundingClientRect().left - 238}px`;
            }
            return;
        }

        // 第二条提示：智能助手按钮
        const assistantEl = document.querySelector('#smart-assistant-switch .assistant-box');
        if (assistantEl) {
            tipEl.style.top = `${assistantEl.getBoundingClientRect().top + 40}px`;
            tipEl.style.left = `${assistantEl.getBoundingClientRect().left - 445}px`;
            this.triangle.nativeElement.style.left = '448px';
            this.circle.nativeElement.style.left = '440px';
        }
    }

    /**
     * 展示下一条提示或者隐藏提示框
     */
    next(): void {
        // 最后一条Tip点击按钮后，销毁Tip组件
        if (Number(this.currentTip) === Number(this.tipsNum)) {
            this.hideTip();
            return;
        }

        // 切换到第二条Tip
        this.currentTip = SimpleModeTipE.SmartAssistant;
        this.tipTitle = this.i18n.assistantTitle;
        this.tipContent = this.i18n.assistantContent;
        this.btnLabel = this.i18n.experienceNow;
        this.backgroundImgUrl = SMART_ASSISTANT_BG_URL;
    }

    hideTip(): void {
        this.visible = false;
        this.onClose.emit();

        // 设置已看过Tip，避免路由切换时再次展示Tip
        this.authService.setShowSimpleModeTip(false);

        // 通知首页的what new或极简的智能助手显示
        this.eventService.emit(EventServiceKeyE.SimpleModeTipHide);
        this.lazyloadService.destroySelf(this);
    }

    setNotShowAgain(): void {
        this.smartAssistantService.setHideTip();
        this.notShowBtnVisible = false;
        this.hideTip();
    }
}
